/**
 * Satellite tracking visualization layer.
 * Renders satellite positions as country-colored dots using ScatterplotLayer,
 * with optional dashed orbital track lines via PathLayer.
 */

import { ScatterplotLayer, PathLayer, TextLayer, PolygonLayer } from '@deck.gl/layers';
import type { Layer } from '@deck.gl/core';
import type { SatellitePosition, SatRecord } from '@/sources/built-in/satellites';
import { propagatePositions, getCachedRecords } from '@/sources/built-in/satellites';

/** Layer IDs handled by this renderer. */
export const SATELLITE_LAYER_IDS = new Set(['satellites', 'satellite-tracks', 'satellite-imagery']);

// ---------------------------------------------------------------------------
// Color parsing
// ---------------------------------------------------------------------------

/** Parse a hex color string to an RGBA tuple. */
function hexToRgba(hex: string, alpha = 200): [number, number, number, number] {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return [r, g, b, alpha];
}

// ---------------------------------------------------------------------------
// Orbital track computation
// ---------------------------------------------------------------------------

/** Number of minutes forward and backward to project orbital path. */
const TRACK_MINUTES = 45;
const TRACK_STEP_MINUTES = 2;

export interface OrbitalTrack {
  id: string;
  country: string;
  color: [number, number, number, number];
  path: [number, number, number][];
}

/**
 * Compute orbital tracks for a subset of visible satellites.
 * Projects positions forward and backward from current time.
 * altitudeScale multiplies the km altitude: the flat map exaggerates
 * x100 for deck.gl visibility; the globe passes 1/6371 for globe.gl's
 * radius-fraction altitude. Shared with the globe bridge.
 */
export function computeOrbitalTracks(
  records: SatRecord[],
  limit: number,
  altitudeScale = 100,
): OrbitalTrack[] {
  const now = new Date();
  const tracks: OrbitalTrack[] = [];
  const subset = records.slice(0, limit);

  for (const rec of subset) {
    const path: [number, number, number][] = [];

    for (let m = -TRACK_MINUTES; m <= TRACK_MINUTES; m += TRACK_STEP_MINUTES) {
      const time = new Date(now.getTime() + m * 60 * 1000);
      const positions = propagatePositions([rec], time);

      if (positions.length > 0) {
        const p = positions[0];
        path.push([p.lng, p.lat, p.alt * altitudeScale]);
      }
    }

    if (path.length > 2) {
      tracks.push({
        id: rec.id,
        country: rec.country,
        color: hexToRgba(rec.color, 120),
        path,
      });
    }
  }

  return tracks;
}

// ---------------------------------------------------------------------------
// Layer factory
// ---------------------------------------------------------------------------

/**
 * Create deck.gl layers for satellite visualization.
 * Returns a ScatterplotLayer for positions and optionally a PathLayer for tracks.
 */
export function createSatelliteLayers(
  data: SatellitePosition[],
  zoom: number,
  showTracks = false,
): Layer[] {
  if (data.length === 0) return [];

  const layers: Layer[] = [];
  const opacity = Math.min(1, 0.4 + zoom * 0.08);

  // --- Satellite position dots ---
  layers.push(new ScatterplotLayer<SatellitePosition>({
    id: 'satellites-dots',
    data,
    pickable: true,
    filled: true,
    stroked: false,
    radiusUnits: 'pixels' as const,
    radiusMinPixels: 2,
    radiusMaxPixels: 8,
    getPosition: (d) => [d.lng, d.lat, d.alt * 100],
    getFillColor: (d) => hexToRgba(d.color, 200),
    getRadius: 5,
    opacity,
    updateTriggers: {
      getFillColor: [data.length],
    },
  }));

  // --- Labels at higher zoom ---
  if (zoom >= 6) {
    // Limit labels to avoid clutter
    const labelData = data.slice(0, 200);
    layers.push(new TextLayer<SatellitePosition>({
      id: 'satellites-labels',
      data: labelData,
      getPosition: (d) => [d.lng, d.lat, d.alt * 100],
      getText: (d) => d.name.length > 30 ? d.name.slice(0, 27) + '...' : d.name,
      getSize: 9,
      getColor: [255, 255, 255, 170],
      getTextAnchor: 'start' as const,
      getAlignmentBaseline: 'center' as const,
      getPixelOffset: [8, 0],
      fontFamily: 'monospace',
      sizeUnits: 'pixels' as const,
      billboard: true,
      outlineWidth: 2,
      outlineColor: [0, 0, 0, 180],
    }));
  }

  // --- Orbital tracks (optional, dashed lines) ---
  if (showTracks) {
    const records = getCachedRecords();
    if (records && records.length > 0) {
      // Limit track computation to avoid performance issues
      const trackLimit = zoom >= 5 ? 50 : 200;
      const tracks = computeOrbitalTracks(records, trackLimit);

      if (tracks.length > 0) {
        layers.push(new PathLayer<OrbitalTrack>({
          id: 'satellite-tracks-lines',
          data: tracks,
          pickable: false,
          getPath: (d) => d.path,
          getColor: (d) => d.color,
          getWidth: 1,
          widthUnits: 'pixels' as const,
          widthMinPixels: 1,
          opacity: opacity * 0.6,
          // Dashed line via spread to bypass strict typing
          ...(({ getDashArray: [4, 4], dashJustified: true }) as Record<string, unknown>),
        }));
      }
    }
  }

  return layers;
}

// ---------------------------------------------------------------------------
// Satellite imagery footprints
// ---------------------------------------------------------------------------

/** Imagery footprint polygon data. */
export interface ImageryFootprint {
  id: string;
  polygon: [number, number][];
  satelliteName?: string;
}

/**
 * Create deck.gl layers for satellite imagery footprint visualization.
 * Shows which ground areas are being observed — matching WorldMonitor.
 */
export function createImageryFootprintLayers(
  footprints: ImageryFootprint[],
  zoom: number,
): Layer[] {
  if (footprints.length === 0) return [];

  const opacity = Math.min(0.8, 0.3 + zoom * 0.06);

  return [
    new PolygonLayer<ImageryFootprint>({
      id: 'satellite-imagery-footprints',
      data: footprints,
      pickable: true,
      filled: true,
      stroked: true,
      getPolygon: (d) => d.polygon,
      getFillColor: [0, 180, 255, 40],      // cyan fill — matching WM
      getLineColor: [0, 180, 255, 180],     // cyan outline — matching WM
      getLineWidth: 1,
      lineWidthMinPixels: 1,
      lineWidthUnits: 'pixels' as const,
      opacity,
    }),
  ];
}
